import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCreateCustomPackage, useUpdateCustomPackage } from '@/api/domains/custom-packages';
import type { CustomPackage } from '@/api/types/custom-packages';
import { FormNumberField, FormTextField } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { applyApiErrors, toApiProblem } from '@/lib/errors';

import { customPackageSchema, type CustomPackageFormValues } from '../schemas';

// Numbers start blank (FormNumberField maps '' to undefined); the schema
// requires them before submit.
const EMPTY: Partial<CustomPackageFormValues> = {
  name: '',
  length: undefined,
  width: undefined,
  height: undefined,
  weight: undefined,
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: CustomPackage | null;
};

export function CustomPackageFormDialog({ open, onOpenChange, pkg }: Props) {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  const create = useCreateCustomPackage();
  const update = useUpdateCustomPackage(pkg?.id ?? 0);

  const form = useForm<CustomPackageFormValues>({
    resolver: zodResolver(customPackageSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      pkg
        ? {
            name: pkg.name,
            length: pkg.length,
            width: pkg.width,
            height: pkg.height,
            weight: pkg.weight,
          }
        : EMPTY,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pkg]);

  const onSubmit = (values: CustomPackageFormValues) => {
    const mutation = pkg ? update : create;
    mutation.mutate(values, {
      onSuccess: () => {
        toast.success(pkg ? t('customPackages.updated') : t('customPackages.created'));
        onOpenChange(false);
      },
      onError: (error) => applyApiErrors(form, toApiProblem(error)),
    });
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {pkg ? t('customPackages.editTitle') : t('customPackages.addTitle')}
          </DialogTitle>
          <DialogDescription>{t('customPackages.formDescription')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 md:grid-cols-3">
            <FormTextField<CustomPackageFormValues>
              name="name"
              label={t('customPackages.fields.name')}
              required
              className="md:col-span-3"
            />
            <FormNumberField<CustomPackageFormValues>
              name="height"
              label={t('customPackages.fields.height')}
              required
              min={0.01}
              step={0.01}
              unit="cm"
            />
            <FormNumberField<CustomPackageFormValues>
              name="width"
              label={t('customPackages.fields.width')}
              required
              min={0.01}
              step={0.01}
              unit="cm"
            />
            <FormNumberField<CustomPackageFormValues>
              name="length"
              label={t('customPackages.fields.length')}
              required
              min={0.01}
              step={0.01}
              unit="cm"
            />
            <FormNumberField<CustomPackageFormValues>
              name="weight"
              label={t('customPackages.fields.weight')}
              required
              min={0.01}
              step={0.01}
              unit="kg"
            />

            <DialogFooter className="md:col-span-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {tCommon('actions.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? tCommon('actions.saving') : tCommon('actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
